import { useState, useEffect } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Grid, TableFooter, Form, Row, InputMask } from 'components'
import { EditIcon, DeleteIcon } from 'components/Icons'
import { SuccessModal } from 'components/SuccessModal/SuccessModal'
import { useModal } from 'hooks'
import useLoading from 'hooks/useLoading'
import useToggle from 'hooks/useToggle'
import { useForm } from 'react-hook-form'
import {
  IContractTypeParams,
  ContractTypeParamsServices
} from 'services/NGT/ContractsParams'
import { SPACING } from 'utils'
import * as Yup from 'yup'

import EditContractTypeModal from './components/EditContractTypeModal/EditContractTypeModal'
import {
  TableContainerStyled,
  TableStyled,
  TableHeadStyled,
  TableRowStyled,
  TableCellStyled,
  TableBodyStyled
} from './ContractTypeSap.styles'

interface IForm {
  contractTypeName: string
}

const schema = Yup.object({
  contractTypeName: Yup.string().required('Tipo contrato é obrigátorio')
})

export const ContractTypeSap = () => {
  const {
    control,
    formState: { errors },
    setValue,
    handleSubmit
  } = useForm<IForm>({
    resolver: yupResolver(schema)
  })
  const { showModal } = useModal()
  const [page, setPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(15)
  const [contractTypes, setContractTypes] = useState([])
  const { loading, msgErro, setLoading, setMsgErro } = useLoading()
  const [isModalOpen, toggleModal] = useToggle()
  const [idContractTypeDel, setIdContractTypeDel] = useState<
    number | undefined
  >()
  const [selectedContractType, setSelectedContractType] = useState<
    IContractTypeParams | undefined
  >()
  const [isDeleteModalOpen, toggleDeleteModal] = useToggle()
  const [openEditContractTypeModal, toggleEditContractTypeModal] = useToggle()

  const handleChangePage = (newPage: number) => setPage(newPage)

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const getAllContractTypesData = () => {
    setLoading('loading')
    ContractTypeParamsServices()
      .getAll()
      .then(({ data }) => {
        setContractTypes(data)
        setLoading('idle')
      })
      .catch((err) => {
        // setMsgErro(err.title)
        showModal({
          hideButtonCancel: true,
          message: err.title || 'Erro ao obter os dados da pesquisa.',
          txtButtonConfirm: 'OK',
          type: 'alert'
        })
        setLoading('error')
      })
  }

  const handleSelectedContractType = (currentContractType: number) => {
    setLoading('loading')

    ContractTypeParamsServices()
      .get(currentContractType)
      .then(({ data }) => {
        setSelectedContractType(data)
        setLoading('idle')
        toggleEditContractTypeModal()
      })
      .catch((err) => {
        // setMsgErro(err.title)
        showModal({
          hideButtonCancel: true,
          message: err.title || 'Erro ao obter os dados da pesquisa.',
          txtButtonConfirm: 'OK',
          type: 'alert'
        })
        setLoading('error')
      })
  }

  const handleSubmitForm = ({ contractTypeName }) => {
    if (contractTypeName.trim() !== '') {
      setLoading('loading')

      ContractTypeParamsServices()
        .create({ dsTipoContrato: contractTypeName })
        .then((_) => {
          setLoading('idle')
          setValue('contractTypeName', '')
          toggleModal()
        })
        .catch((err) => {
          showModal({
            hideButtonCancel: true,
            message: 'Erro ao criar Tipo Contrato, tente novamente!',
            titleModal: 'Atenção',
            txtButtonConfirm: 'Fechar',
            type: 'error'
          })
          setLoading('idle')
        })
    }
  }

  const handleEditContractType = (requestData: IContractTypeParams) => {
    setLoading('loading')
    ContractTypeParamsServices()
      .patch(requestData)
      .then((_) => {
        setLoading('idle')
        toggleEditContractTypeModal()
        getAllContractTypesData()
        showModal({
          hideButtonCancel: true,
          message: 'Atualizado com sucesso',
          txtButtonConfirm: 'Fechar',
          type: 'success'
        })
      })
      .catch(() => {
        showModal({
          hideButtonCancel: true,
          message: 'Erro ao tentar atualizar dados, tente novamente!',
          titleModal: 'Atenção',
          txtButtonConfirm: 'Fechar',
          type: 'error'
        })
        setLoading('idle')
      })
  }

  const handleOpenDeleteModal = (idContractType: number) => {
    toggleDeleteModal()
    setIdContractTypeDel(idContractType)
  }

  const handleDeleteContractType = () => {
    toggleDeleteModal()
    setLoading('loading')
    ContractTypeParamsServices()
      .del(idContractTypeDel)
      .then(({ data }) => {
        if (data?.sucesso) {
          handleChangePage(0)
          getAllContractTypesData()
          showModal({
            hideButtonCancel: true,
            message: 'Excluído com sucesso',
            txtButtonConfirm: 'Fechar',
            type: 'success'
          })
        }
        setLoading('idle')
      })
      .catch(() => {
        showModal({
          hideButtonCancel: true,
          message: 'Erro ao deletar, tente novamente!',
          titleModal: 'Atenção',
          txtButtonConfirm: 'Fechar',
          type: 'error'
        })
        setLoading('idle')
      })
  }

  useEffect(() => {
    getAllContractTypesData()
  }, [])

  return (
    <>
      <Grid
        gridCols="1fr"
        gridGap={`0 ${SPACING.spacing20}`}
        margin={`${SPACING.spacing20} 0 ${SPACING.spacing30} 0`}
      >
        <Form
          onSubmit={handleSubmit(handleSubmitForm)}
          marginLeft={SPACING.spacing20}
          marginRight={SPACING.spacing20}
          marginBottom={SPACING.spacing10}
          padding={SPACING.spacing20}
        >
          <Row alignItems="center">
            <InputMask
              label="Tipo Contrato SAP"
              control={control}
              errors={errors}
              width="400px"
              formValue="contractTypeName"
              placeholder="Adicionar Tipo Contrato SAP..."
              maxLength={40}
              type="alfanumeric"
            />
            <div style={{ width: SPACING.spacing20 }}></div>
            <Button type="submit" text="Adicionar" />
          </Row>
        </Form>
      </Grid>
      <Grid
        gridCols="5fr 3fr"
        gridGap={`0 ${SPACING.spacing10}`}
        margin={`0 0 ${SPACING.spacing30} 0`}
      >
        <TableContainerStyled>
          <TableStyled stickyHeader>
            <TableHeadStyled>
              <TableRowStyled>
                <TableCellStyled minwidth="18em">Contratos SAP</TableCellStyled>
                <TableCellStyled>Ações</TableCellStyled>
              </TableRowStyled>
            </TableHeadStyled>
            <TableBodyStyled>
              {(rowsPerPage > 0
                ? contractTypes.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                : contractTypes
              )?.map((row) => (
                <TableRowStyled key={row?.idTipoContrato?.toString()}>
                  <TableCellStyled>{row.dsTipoContrato}</TableCellStyled>
                  <TableCellStyled>
                    <EditIcon
                      onClick={() =>
                        handleSelectedContractType(row.idTipoContrato)
                      }
                      marginRight={SPACING.spacing15}
                    />
                    <DeleteIcon
                      cursor="pointer"
                      onClick={() => handleOpenDeleteModal(row.idTipoContrato)}
                    />
                  </TableCellStyled>
                </TableRowStyled>
              ))}
            </TableBodyStyled>
          </TableStyled>
          <TableFooter
            page={page}
            rows={contractTypes}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            typeFooter="offline"
          />
        </TableContainerStyled>
      </Grid>
      <SuccessModal
        open={loading === 'error'}
        message={msgErro}
        type="error"
        hideButtonCancel
        handleConfirm={() => setLoading('idle')}
        txtButtonConfirm="Voltar"
      />
      <SuccessModal
        open={isModalOpen}
        message="Tipo Contrato incluído com sucesso!"
        type="sucess"
        hideButtonCancel
        txtButtonConfirm="OK"
        handleConfirm={() => {
          toggleModal()
          getAllContractTypesData()
        }}
      />
      <EditContractTypeModal
        showModal={openEditContractTypeModal}
        toggleModal={toggleEditContractTypeModal}
        contractTypesData={selectedContractType}
        handleSubmitUpdate={handleEditContractType}
      />
      <SuccessModal
        open={isDeleteModalOpen}
        message="Confirma exclusão (S/N)?"
        type="alert"
        txtButtonCancel="NÃO"
        txtButtonConfirm="SIM"
        handleCancel={toggleDeleteModal}
        handleConfirm={handleDeleteContractType}
      />
    </>
  )
}
